<template>
  <b-modal
    :id="modalId"
    :aria-label="modalTitle"
    modal-class="select-folder-modal"
    ok-only
    :ok-disabled="!selectedItems.length"
    @show="applyInitialStockTab"
    @ok="handleOkClick"
    @hidden="resetModalState"
  >
    <template #modal-header="{ close }">
      <div class="select-folder-modal__header">
        <h5 class="select-folder-modal__title">
          {{ modalTitle }}
        </h5>
        <b-tabs
          v-show="viewTabs"
          pills
          class="c-file-browser select-folder-modal__tabs"
        >
          <b-tab
            title="Files"
            :active="currentTab === TABS.FILES"
            @click="navigate(TABS.FILES)"
          />
          <b-tab
            title="Products"
            :active="currentTab === TABS.PRODUCTS"
            @click="navigate(TABS.PRODUCTS)"
          />
          <b-tab
            title="Stock"
            :active="currentTab === TABS.STOCK"
            @click="navigate(TABS.STOCK)"
          />
          <b-tab
            v-if="isSalsifyAvailable"
            title="Salsify Products"
            :active="currentTab === TABS.SALSIFY_PRODUCTS"
            @click="navigate(TABS.SALSIFY_PRODUCTS)"
          />
          <b-tab
            v-if="isSalsifyAvailable"
            title="Salsify Assets"
            :active="currentTab === TABS.SALSIFY_ASSETS"
            @click="navigate(TABS.SALSIFY_ASSETS)"
          />
          <b-tab
            v-if="isBoxAvailable"
            title="Box Assets"
            :active="currentTab === TABS.BOX_ASSETS"
            @click="navigate(TABS.BOX_ASSETS)"
          />
          <b-tab
            title="Briefs"
            :active="currentTab === TABS.BRIEF"
            @click="navigate(TABS.BRIEF)"
          />
          <b-tab
            v-if="showLayouts"
            title="Layouts"
            :active="currentTab === TABS.LAYOUTS"
            @click="navigate(TABS.LAYOUTS)"
          />
        </b-tabs>
        <button
          type="button"
          class="close select-folder-modal__close"
          aria-label="Close"
          @click="close()"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </template>
    <SelectFolderModalContentView
      v-if="isFolderTab"
      :allow-multiple-selection="mode !== 'selectAndCreate'"
      :is-light-weight="isLightWeight"
      @onSelectedItems="(items) => selectedItems = items"
    />
    <ExternalContentView
      v-if="isStockTab"
      :endpoint="stockEndpoint"
      @onSelectedItems="(items) => selectedItems = items"
    >
      <template #toolbar-start>
        <b-tabs
          pills
          class="c-file-browser c-file-browser--stock"
        >
          <b-tab
            title="It'sRapid"
            :active="currentStockTab === TABS.ITS_RAPID_STOCK"
            @click="navigateStock(TABS.ITS_RAPID_STOCK)"
          />
          <b-tab
            title="Pixabay"
            :active="currentStockTab === TABS.PIXABAY_STOCK"
            @click="navigateStock(TABS.PIXABAY_STOCK)"
          />
        </b-tabs>
      </template>
    </ExternalContentView>
    <template v-if="isSalsifyTab">
      <ExternalContentView
        :endpoint="salsifyEndpoint"
        @onSelectedItems="(items) => selectedItems = items"
        @autoConfirm="handleOkClick"
      />
    </template>
    <ExternalContentView
      v-if="isBoxTab"
      :endpoint="boxEndpoint"
      navigable-folders
      @onSelectedItems="(items) => selectedItems = items"
    />
    <BriefsTabContentView
      v-if="isBriefTab"
      :default-brief-id="defaultBriefId"
      @onSelectedItems="(items) => selectedItems = items"
    />
    <LayoutsContentView
      v-if="isLayoutsTab"
      @onLayoutLayers="handleLayoutLayers"
    />
    <template
      v-if="showCreateLayers"
      #modal-footer="{ cancel }"
    >
      <b-form-checkbox
        v-model="createLayers"
        switch
        class="mr-auto"
      >
        Create Layers
      </b-form-checkbox>
      <b-button
        variant="outline-secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="!selectedItems.length || (createLayers && selectedItems.length !== 1)"
        @click="handleOkClick"
      >
        OK
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import { FILE_TYPE } from '../../../../constants/file-image-types';
import SelectFolderModalContentView from './SelectFolderModalContentView.vue';
import ExternalContentView from './ExternalContentView.vue';
import LayoutsContentView from './LayoutsContentView.vue';
import BriefsTabContentView from './BriefsTabContentView.vue';
import { TABS } from '../constants';
import FileBrowser from '@frontend/services/api/file-browser';

const EXTERNAL_SOURCES = {
  SALSIFY: 'salsify',
  BOX: 'box',
};

export default {
  components: { SelectFolderModalContentView, ExternalContentView, LayoutsContentView, BriefsTabContentView },
  props: {
    mode: {
      type: String,
      default: 'default'
    },
    viewTabs: {
      type: Boolean,
      default: false
    },
    isLightWeight: {
      type: Boolean,
      default: false
    },
    modalTitle: {
      type: String,
      default: 'Select Folder'
    },
    modalId: {
      type: String,
      default: 'select-folder-modal'
    },
    // Optional: open the modal directly on a given tab. Accepts a top-level tab
    // (e.g. 'salsify-products' / 'salsify-assets') or a Stock sub-tab.
    initialStockTab: {
      type: String,
      default: ''
    },
    showLayouts: {
      type: Boolean,
      default: true
    },
    showCreateLayers: {
      type: Boolean,
      default: false
    },
    defaultBriefId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      selectedItems: [],
      createLayers: false,
      TABS,
      currentTab: TABS.FILES,
      currentStockTab: TABS.ITS_RAPID_STOCK,
      isSalsifyAvailable: false,
      isBoxAvailable: false
    }
  },
  computed: {
    isFolderTab() {
      return [TABS.FILES, TABS.PRODUCTS].includes(this.currentTab);
    },
    isStockTab() {
      return this.currentTab === TABS.STOCK;
    },
    isSalsifyTab() {
      return [TABS.SALSIFY_ASSETS, TABS.SALSIFY_PRODUCTS].includes(this.currentTab);
    },
    isBoxTab() {
      return this.currentTab === TABS.BOX_ASSETS;
    },
    isLayoutsTab() {
      return this.currentTab === TABS.LAYOUTS;
    },
    isBriefTab() {
      return this.currentTab === TABS.BRIEF;
    },
    stockEndpoint() {
      if (this.currentStockTab === TABS.PIXABAY_STOCK) {
        return '/stock/retrieve-pixabay';
      }

      return '/stock/retrieve-itsrapid';
    },
    salsifyEndpoint() {
      return this.currentTab === TABS.SALSIFY_ASSETS
        ? '/salsify/retrieve-assets'
        : '/salsify/retrieve-products';
    },
    boxEndpoint() {
      return '/box/folder-content';
    },
  },

  async mounted () {
    // Auth-only endpoint — guests (e.g. shared brief editors) must not surface a toast.
    this.isSalsifyAvailable = await this.isExternalSourceAvailable(EXTERNAL_SOURCES.SALSIFY);
    this.isBoxAvailable = await this.isExternalSourceAvailable(EXTERNAL_SOURCES.BOX);
  },

  methods: {
    async handleOkClick() {
      if (this.selectedItems.length === 1 && this.selectedItems[0].type === FILE_TYPE.FOLDER) {
        this.$emit('onSelectedItems', this.selectedItems);
        this.$bvModal.hide(this.modalId);

        return;
      }

      let allFilesToAdd = [];

      for (const item of this.selectedItems) {
        if (item.type === FILE_TYPE.FOLDER) {
          const folderFiles = await this.getDirectFilesFromFolder(item.id);
          allFilesToAdd = [...allFilesToAdd, ...folderFiles];
        } else {
          allFilesToAdd.push(item);
        }
      }

      allFilesToAdd = await this.resolveBoxAssets(allFilesToAdd);

      if (this.showCreateLayers && this.createLayers && allFilesToAdd.length !== 1) {
        toastr.warning('Select one image to create layers');
        return;
      }

      const options = {
        createLayers: this.createLayers
      }
      this.$emit('onSelectedItems', allFilesToAdd, options);
      
      this.$bvModal.hide(this.modalId);
    },

    async resolveBoxAssets(items) {
      const boxItems = items.filter((item) => item && item.type === FILE_TYPE.BOX_FILE);

      if (!boxItems.length) {
        return items;
      }

      let urlById = new Map();

      try {
        const { data } = await FileBrowser.importBoxAssets(boxItems.map((item) => ({
          id: item.id,
          name: item.name,
          extension: item.extension,
          download_url: item.download_url,
        })));

        urlById = new Map(((data && data.files) || []).map((file) => [String(file.id), file.url]));
      } catch (error) {
        toastr.error('Failed to load the selected Box file(s).');

        return items.filter((item) => !item || item.type !== FILE_TYPE.BOX_FILE);
      }

      return items
        .map((item) => {
          if (!item || item.type !== FILE_TYPE.BOX_FILE) {
            return item;
          }

          const url = urlById.get(String(item.id));

          if (!url) {
            return null;
          }

          return {
            ...item,
            path: url,
            url,
            original_path: url,
            download_url: url,
            _boxResolved: true,
          };
        })
        .filter(Boolean);
    },

    async getDirectFilesFromFolder(folderId, pageNumber = 1, allFiles = []) {
      try {
        const response = await axios.get('/file/folder_content', {
          params: {
            folderId: folderId,
            searchKey: '',
            pageNumber: pageNumber,
            pageSize: 100,
            folder_id: 0
          }
        });

        const data = response.data;
        const items = data.items || [];

        for (const item of items) {
          if (item.id === null && item.is_root === true) {
            continue;
          }

          if (item.type !== FILE_TYPE.FOLDER && item.type !== FILE_TYPE.PSD) {
            allFiles.push(item);
          }
        }

        if (data.current_page < data.last_page) {
          return await this.getDirectFilesFromFolder(folderId, pageNumber + 1, allFiles);
        }

        return allFiles;
      } catch (error) {
        console.error('Error fetching folder contents:', error);
        toastr.error('Failed to fetch folder contents');
        return allFiles;
      }
    },
    handleLayoutLayers(payload) {
      this.$emit('onLayoutLayers', payload);
      this.$bvModal.hide(this.modalId);
    },

    navigate(tab) {
      if (this.currentTab === tab) {
        return;
      }

      this.currentTab = tab;
      this.selectedItems = [];

      if ([TABS.FILES, TABS.PRODUCTS].includes(tab)) {
        this.$store.commit('fileUploadImages/SET_TAB', tab);
      }
    },
    navigateStock(tab) {
      if (this.currentStockTab === tab) {
        return;
      }

      this.currentStockTab = tab;
      this.selectedItems = [];
    },
    // Deep-link: when opened with initialStockTab, jump straight to that tab.
    // Salsify Products/Assets are TOP-LEVEL tabs, so select them via currentTab;
    // It'sRapid/Pixabay are Stock sub-tabs. resetModalState() restores defaults.
    applyInitialStockTab() {
      if (!this.initialStockTab) {
        return;
      }
      if ([TABS.SALSIFY_PRODUCTS, TABS.SALSIFY_ASSETS, TABS.BOX_ASSETS].includes(this.initialStockTab)) {
        this.currentTab = this.initialStockTab;
      } else {
        this.currentTab = TABS.STOCK;
        this.currentStockTab = this.initialStockTab;
      }
      this.selectedItems = [];
    },
    resetModalState() {
      this.createLayers = false;
      if (this.viewTabs) {
        this.selectedItems = [];
        this.currentTab = TABS.FILES;
        this.currentStockTab = TABS.ITS_RAPID_STOCK;
        this.$store.commit('fileUploadImages/SET_TAB', TABS.FILES);
      }

      this.$emit('hidden');
    },
    async isExternalSourceAvailable(source) {
      try {
        const { data } = await axios.post(
          '/platforms/is-platform-available',
          { platform: source },
          { skipErrorToast: true },
        );

        return Boolean(data && data.available);
      } catch {
        return false;
      }
    }
  }
}
</script>
<style scoped>
::v-deep .select-folder-modal .modal-dialog {
  max-width: 960px;
  margin: 1rem auto;
}

::v-deep .select-folder-modal .modal-content {
  max-height: calc(100vh - 2rem);
  display: flex;
}

::v-deep .select-folder-modal .modal-body {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.select-folder-modal__header {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 20px;
}

.select-folder-modal__title {
  flex: 0 0 auto;
  margin: 0;
  font-family: "Nunito", sans-serif;
  white-space: nowrap;
  color: #0f172a;
}

.select-folder-modal__tabs {
  flex: 1 1 auto;
  min-width: 0;
}

.select-folder-modal__close {
  flex: 0 0 auto;
  margin-left: auto;
}

/* No max-height: the grid sizes the modal, `.modal-content` caps it at the viewport. */
::v-deep .select-folder-modal .folder-list-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

::v-deep .select-folder-modal .folder-sticky-header {
  position: sticky;
  top: 0;
  padding-bottom: 12px;
  background: #fff;
  z-index: 4;
}

::v-deep .select-folder-modal .folder-scroll-area {
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 auto;
  padding-right: 4px;
}

::v-deep .select-folder-modal .folder-sticky-footer {
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 4;
  padding-top: 8px;
}
::v-deep .c-file-browser {
  padding-right: 1rem;
  font-family: "Nunito", sans-serif;
}

::v-deep .c-file-browser--stock {
  padding-right: 0;
}

::v-deep .c-file-browser--stock .nav-pills {
  margin-bottom: 0;
}
::v-deep .item-pagination ul {
  margin-bottom: 0 !important;
}
::v-deep .c-file-browser .nav-pills {
  width: fit-content;
  border-bottom: none;
  margin-bottom: 10px;
  background-color: #f0f2f4;
  border-radius: 6px;
  padding: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  font-size: 14px;
  display: flex;
  align-items: center;
}

::v-deep .c-file-browser .nav-pills a.nav-link {
  font-weight: 500;
  color: #8a929a;
  border: none;
  background-color: transparent;
  transition: all 0.2s ease;
  padding: 4px 10px;
}

::v-deep .c-file-browser .nav-pills a.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.6);
  color: rgb(34, 38, 42);
}

::v-deep .select-folder-modal__tabs.c-file-browser .nav-pills {
  width: 100%;
  margin-bottom: 0;
  background-color: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  gap: 4px;
}

::v-deep .select-folder-modal__tabs.c-file-browser .nav-pills a.nav-link {
  padding: 6px 12px;
  line-height: 20px;
  border-radius: 6px;
  color: #64748b;
}

::v-deep .select-folder-modal__tabs.c-file-browser .nav-pills a.nav-link:hover {
  background-color: #f0f2f4;
  color: rgb(34, 38, 42);
}

::v-deep .select-folder-modal__tabs.c-file-browser .nav-pills a.nav-link.active {
  padding: 6px 12px;
  line-height: 20px;
  background-color: rgba(22, 163, 139, 0.12);
  color: #16a38b;
}

::v-deep .select-folder-modal__tabs.c-file-browser .nav-pills a.nav-link.active:hover {
  background-color: rgba(22, 163, 139, 0.12);
  color: #16a38b;
}

::v-deep .c-file-browser .nav-pills a.nav-link.active {
  padding: 4px 10px;
  border-radius: 6px;
  background-color: #ffffff;
  color: rgb(34, 38, 42);
  font-weight: 700;
  pointer-events: none;
  border: none;
}
</style>
